import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import Logo from './Logo';
import HelpGuide from './HelpGuide';
import './Header.css';

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserDropdownOpen(false);
  }, [location]);

  // Fetch cart count when user is logged in
  useEffect(() => {
    const fetchCartCount = async () => {
      if (user && user.user_type !== 'admin') {
        try {
          const response = await api.get('/cart');
          const totalItems = response.data.cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
          setCartCount(totalItems);
        } catch (err) {
          // If cart fetch fails, set count to 0
          setCartCount(0);
        }
      } else {
        setCartCount(0);
      }
    };

    fetchCartCount();
    
    // Refresh cart count when location changes (user might have added/removed items)
    // Also set up a custom event listener for cart updates
    const handleCartUpdate = () => {
      fetchCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [user, location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          <Logo />
          <button 
            className="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <Link to="/schools" className="nav-link">
              <span className="nav-icon">🏫</span>
              <span className="nav-text">Browse Schools</span>
            </Link>
            {user ? (
              <>
                {user.user_type !== 'admin' && (
                  <>
                    <Link 
                      to="/cart"
                      className="nav-link cart-link mobile-cart-link"
                      onClick={(e) => {
                        // On mobile with empty cart, go to schools instead
                        if (isMobile && cartCount === 0) {
                          e.preventDefault();
                          alert('Your cart is empty. Please add items to cart first.');
                          navigate('/schools');
                        }
                      }}
                    >
                      <span className="nav-icon">🛒</span>
                      <span className="nav-text">Cart</span>
                      {cartCount > 0 && (
                        <span className="cart-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                      )}
                    </Link>
                    <Link to="/orders" className="nav-link">
                      <span className="nav-icon">📦</span>
                      <span className="nav-text">Orders</span>
                    </Link>
                  </>
                )}
                {user.user_type && user.user_type.toLowerCase() === 'admin' && (
                  <>
                    <Link to="/admin" className="nav-link admin-link">
                      <span className="nav-icon">⚙️</span>
                      <span className="nav-text">Dashboard</span>
                    </Link>
                    <Link to="/reports" className="nav-link reports-link">
                      <span className="nav-icon">📊</span>
                      <span className="nav-text">Reports</span>
                    </Link>
                  </>
                )}
                <Link to="/contact" className="nav-link">
                  <span className="nav-icon">📞</span>
                  <span className="nav-text">Contact</span>
                </Link>
                <div className="user-menu-container" ref={dropdownRef}>
                  <button 
                    className="user-greeting user-greeting-button"
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  >
                    <span className="greeting-icon">👋</span>
                    <span className="greeting-text">Hello, {user.full_name}</span>
                    <svg 
                      className={`dropdown-arrow ${isUserDropdownOpen ? 'open' : ''}`}
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {isUserDropdownOpen && (
                    <div className="user-dropdown">
                      <Link 
                        to="/change-password" 
                        className="dropdown-item"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                        <span>Change Password</span>
                      </Link>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="nav-link nav-link-button logout-btn"
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  aria-label="Logout"
                >
                  <span className="nav-icon">🚪</span>
                  <span className="nav-text">Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link">
                  <span className="nav-icon">🔐</span>
                  <span className="nav-text">Login</span>
                </Link>
                <Link to="/contact" className="nav-link">
                  <span className="nav-icon">📞</span>
                  <span className="nav-text">Contact</span>
                </Link>
                <Link to="/register" className="btn btn-primary btn-register">
                  <span>Get Started</span>
                  <span className="btn-arrow">→</span>
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>
      
      {/* Floating Help Button - Available on all pages */}
      <button 
        className="help-floating-button"
        onClick={() => setIsHelpOpen(true)}
        aria-label="Open Help Guide"
        title="Need Help? Click Here!"
      >
        <span className="help-icon">❓</span>
      </button>

      {/* Help Guide Modal */}
      <HelpGuide isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </header>
  );
};

export default Header;

